import React from "react";

import { toast } from "@performyard/py-components";

import { AdminContainer, AdminTitle } from "../../../../components/Admin";
import PageTitle from "../../../../components/PageTitle";
import Spacer from "../../../../components/Spacer";

import { useMeetingsIntegrationsSettings, useUpdateMeetingsIntegrationsSettings } from "../../../../api/hooks";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";

import ErrorPage from "../../../Error";
import { SectionLayout, SettingsToggle } from "../../Components";

const PAGE_TITLE = "Meetings Integrations";

const MeetingsIntegrations = (): React.ReactElement => {
	const { value: calendarIntegrationFeatureVisible } = useFeatureFlagQuery(
		"ff-is-calendar-integration-enabled",
		false
	);
	const { value: meetingIntegrationsEnabled } = useFeatureFlagQuery("ff-is-meetings-integrations-enabled", false);
	// TEMP (PY-12499): ephemeral gate for the Zoom rollout — remove this FF read and
	// the `videoConferencingToggleVisible` AND-gate below when
	// `ff-temp-zoom-meetings-module-integration` is retired.
	const { value: zoomMeetingsModuleEnabled } = useFeatureFlagQuery("ff-temp-zoom-meetings-module-integration", false);

	const pageVisible = calendarIntegrationFeatureVisible || meetingIntegrationsEnabled;
	// Defense in depth: gate each toggle on its own FF state here in the component.
	// The API response shape (presence/absence of sections) also encodes this gating,
	// but keeping the explicit check makes the rendering intent obvious and prevents
	// the toggles from rendering for one render-frame while the query is in flight.
	const calendarToggleVisible = calendarIntegrationFeatureVisible;
	const videoConferencingToggleVisible = meetingIntegrationsEnabled && zoomMeetingsModuleEnabled;

	const { data: meetingsIntegrationsSettings } = useMeetingsIntegrationsSettings({ enabled: pageVisible });

	const { mutateAsync: updateSettings } = useUpdateMeetingsIntegrationsSettings();

	if (!pageVisible) {
		return <ErrorPage code={404} />;
	}

	// Success toast fires here after the mutation resolves so we can describe
	// which setting changed. On failure, mutateAsync rethrows and SettingsToggle's
	// try/catch fires its generic "Unable to save settings." error toast — no
	// error toast in the mutation hook itself, matching the AI Assistants pattern.
	const handleCalendarToggle = async (enabled: boolean): Promise<void> => {
		await updateSettings({ calendar: { enabled } });
		toast.success(`Calendar Integration ${enabled ? "Enabled" : "Disabled"}`);
	};

	const handleVideoConferencingToggle = async (enabled: boolean): Promise<void> => {
		await updateSettings({ video_conferencing: { enabled } });
		toast.success(`Video Conferencing & Transcripts ${enabled ? "Enabled" : "Disabled"}`);
	};

	return (
		<AdminContainer>
			<PageTitle title={PAGE_TITLE} />
			<Spacer height={4} />
			<AdminTitle>{PAGE_TITLE}</AdminTitle>
			<Spacer height={3} />
			<SectionLayout
				title="Meetings Integrations Settings"
				description="Control whether members of your organization can link their work calendar and video conferencing accounts in PerformYard."
			>
				{calendarToggleVisible && (
					<SettingsToggle
						id="calendar-integration-toggle"
						label="Enable Calendar Integration"
						description="Disabling this setting will hide the ability for users to link their work calendar in PerformYard"
						checked={meetingsIntegrationsSettings?.calendar?.enabled ?? false}
						onCheckedChange={handleCalendarToggle}
						dataTestId="calendar-integration-toggle"
					/>
				)}
				{videoConferencingToggleVisible && (
					<SettingsToggle
						id="video-conferencing-toggle"
						label="Enable Video Conferencing & Transcripts"
						description="Disabling this setting will hide the ability for users to link their Zoom and Teams accounts to generate meeting links and retrieve transcripts."
						checked={meetingsIntegrationsSettings?.video_conferencing?.enabled ?? false}
						onCheckedChange={handleVideoConferencingToggle}
						dataTestId="video-conferencing-toggle"
					/>
				)}
			</SectionLayout>
		</AdminContainer>
	);
};

export default MeetingsIntegrations;
